import { useEffect, useState } from "react";
import { api } from "@/auth/AuthContext";

const fmt = (n, cur) =>
  n === null || n === undefined ? "—" : `${cur || "USD"} ${Number(n).toFixed(2)}`;

const STATUS_STYLES = {
  issued: "bg-emerald-50 text-emerald-800 border-emerald-200",
  refunded_reimbursed: "bg-blue-50 text-blue-800 border-blue-200",
  to_be_paid: "bg-amber-50 text-amber-900 border-amber-200",
  reviewing: "bg-amber-50 text-amber-900 border-amber-200",
};

const Countdown = ({ deadlineUtc }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!deadlineUtc) return null;
  const secs = Math.floor((new Date(deadlineUtc).getTime() - now) / 1000);
  if (secs <= 0)
    return <span className="text-xs font-semibold text-red-700" data-testid="deadline-expired">Deadline passed</span>;
  const m = Math.floor(secs / 60);
  return (
    <span className="text-xs font-semibold text-[#8A1F2C]" data-testid="deadline-countdown">
      Confirm before {new Date(deadlineUtc).toLocaleString()} · {m > 59 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m ${secs % 60}s`} remaining
    </span>
  );
};

export default function FlightOrders() {
  const [orders, setOrders] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/flights/orders`)
      .then((r) => {
        setOrders(r.data.orders || []);
        setIsAdmin(!!r.data.is_admin);
      })
      .catch((e) => setError(e?.response?.status === 401 ? "Please sign in to view flight orders." : "Unable to load flight orders."));
  }, []);

  return (
    <main className="min-h-screen bg-[#F7F5F1] pt-28 pb-16">
      <div className="max-w-5xl mx-auto px-6">
        <p className="text-xs tracking-[0.3em] uppercase text-[#8A1F2C] font-semibold">Book Travel · Sandbox</p>
        <h1 className="text-3xl md:text-4xl font-serif text-[#12275E] mt-2" data-testid="flight-orders-title">
          Flight Orders {isAdmin && <span className="text-sm align-middle font-sans bg-[#12275E] text-white px-2 py-1 ml-2">ADMIN VIEW</span>}
        </h1>
        {error && <p className="mt-6 text-sm text-red-700" data-testid="flight-orders-error">{error}</p>}
        <div className="mt-8 space-y-4">
          {orders.map((o) => {
            const refund = o.refund;
            const badge = STATUS_STYLES[o.status] || "bg-gray-50 text-gray-700 border-gray-200";
            return (
              <div key={o.order_id} className="bg-white border border-[#EAE6DF] p-6" data-testid={`flight-order-${o.order_id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-semibold text-[#12275E]" data-testid="order-id">{o.order_id}</span>
                  <span className={`text-xs border px-2 py-0.5 uppercase tracking-wide ${badge}`} data-testid="order-status">{o.status}</span>
                  {o.order_type === "refund" && (
                    <span className="text-xs border border-[#12275E] text-[#12275E] px-2 py-0.5 uppercase">Refund Order</span>
                  )}
                  {(o.pnrs || []).map((p) => (
                    <span key={p} className="text-xs text-gray-600">PNR {p}</span>
                  ))}
                </div>
                {o.order_type !== "refund" && (
                  <p className="mt-2 text-sm text-gray-700" data-testid="order-total">
                    Total: <strong>{fmt(o.total_price, o.currency)}</strong>
                  </p>
                )}
                {["to_be_paid", "rfd_to_be_confirm"].includes(o.status) && (
                  <div className="mt-2"><Countdown deadlineUtc={o.action_deadline_utc} /></div>
                )}
                {refund && (
                  <div className="mt-3 border-t border-[#EAE6DF] pt-3">
                    {refund.requires_manual_review ? (
                      <div className="bg-red-50 border border-red-200 p-3" data-testid="refund-manual-review">
                        <p className="text-sm font-semibold text-red-800">⚠ Manual Review Required</p>
                        {isAdmin && <p className="text-xs text-red-700 mt-1" data-testid="refund-review-reason">{refund.review_reason}</p>}
                        {!isAdmin && (
                          <p className="text-xs text-red-700 mt-1">
                            This refund is under review by our team. The final amount will be confirmed shortly.
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-800" data-testid="refund-net-amount">
                        Net Refund Amount: <strong className="text-[#12275E]">{fmt(refund.refund_price, refund.currency)}</strong>
                      </p>
                    )}
                    {isAdmin && (
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs" data-testid="refund-admin-breakdown">
                        <div><p className="text-gray-500">Refundable</p><p className="font-semibold">{fmt(refund.refundable_amount, refund.currency)}</p></div>
                        <div><p className="text-gray-500">Used</p><p className="font-semibold">{fmt(refund.used_amount, refund.currency)}</p></div>
                        <div><p className="text-gray-500">Refund Fee</p><p className="font-semibold">{fmt(refund.refund_fee, refund.currency)}</p></div>
                        <div><p className="text-gray-500">Service Fee</p><p className="font-semibold">{fmt(refund.service_fee, refund.currency)}</p></div>
                        <div><p className="text-gray-500">refundPrice (authoritative)</p><p className={`font-semibold ${refund.refund_price < 0 ? "text-red-700" : "text-[#12275E]"}`}>{fmt(refund.refund_price, refund.currency)}</p></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {!error && orders.length === 0 && (
            <p className="text-sm text-gray-600" data-testid="flight-orders-empty">No flight orders yet.</p>
          )}
        </div>
      </div>
    </main>
  );
}
